import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';

const { 
  FiTrendingUp, FiUsers, FiDollarSign, FiBookOpen, 
  FiPlus, FiArrowRight, FiActivity, FiZap, FiCalendar,
  FiBell, FiChevronRight, FiMousePointer
} = FiIcons;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const item = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  show: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { type: "spring", stiffness: 100 }
  }
};

export default function Dashboard() {
  const [stats, setStats] = useState([
    { label: 'Net Revenue', value: '$24,500', trend: '+12.5%', icon: FiDollarSign, color: 'indigo' },
    { label: 'Active Students', value: '142', trend: '+5.2%', icon: FiUsers, color: 'emerald' },
    { label: 'Course Sales', value: '89', trend: '+18.1%', icon: FiBookOpen, color: 'violet' },
    { label: 'LTV Average', value: '$1,240', trend: '+4.3%', icon: FiTrendingUp, color: 'rose' },
  ]);

  return (
    <div className="min-h-screen mesh-bg">
      <div className="noise-overlay" />
      
      <motion.div 
        variants={container}
        initial="hidden"
        animate="show"
        className="max-w-7xl mx-auto space-y-12 pb-24"
      >
        <motion.div variants={item} className="bg-indigo-600/10 border border-indigo-500/20 px-6 py-3 rounded-full flex items-center justify-between group cursor-pointer hover:bg-indigo-600/20 transition-all">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
            </span>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-300">SessionHQ Intelligence Systems Active</p>
          </div>
          <SafeIcon icon={FiChevronRight} className="text-indigo-400 group-hover:translate-x-1 transition-transform" />
        </motion.div>

        <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
          <div>
            <motion.div variants={item} className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                <SafeIcon icon={FiZap} />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400/80">SessionHQ Enterprise v2.1</span>
            </motion.div>
            <motion.h1 variants={item} className="text-6xl font-black text-white tracking-tighter uppercase leading-none">
              Command <span className="shimmer-text">Center</span>
            </motion.h1>
            <motion.p variants={item} className="text-slate-500 mt-4 text-xl font-medium max-w-lg">
              Welcome back. Your SessionHQ environment is currently operating at <span className="text-white font-bold">94% efficiency</span>.
            </motion.p>
          </div>
          <motion.div variants={item} className="flex gap-4">
            <button className="bg-white/5 border border-white/5 hover:border-white/20 text-white px-8 py-5 rounded-[2rem] font-black transition-all flex items-center gap-3 group backdrop-blur-xl">
              <SafeIcon icon={FiBell} className="group-hover:rotate-12 transition-transform" />
              Notifications
            </button>
            <button className="premium-gradient text-white px-10 py-5 rounded-[2rem] font-black flex items-center gap-3 shadow-2xl shadow-indigo-600/40 hover:scale-[1.02] active:scale-95 transition-all">
              <SafeIcon icon={FiPlus} />
              Scale Platform
            </button>
          </motion.div>
        </header>

        <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <motion.div 
              whileHover={{ y: -8 }}
              key={idx} 
              className="glass-card p-8 rounded-[40px] group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-[80px] -mr-16 -mt-16 transition-all group-hover:bg-indigo-500/20" />
              <div className="flex justify-between items-start mb-8">
                <div className="w-14 h-14 bg-white/[0.03] border border-white/10 rounded-2xl flex items-center justify-center text-indigo-400 group-hover:bg-indigo-500 group-hover:text-white transition-all duration-500">
                  <SafeIcon icon={stat.icon} className="text-2xl" />
                </div>
                <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-black tracking-widest ${stat.trend.startsWith('+') ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                  {stat.trend}
                  <SafeIcon icon={FiTrendingUp} />
                </div>
              </div>
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mb-2">{stat.label}</p>
              <h3 className="text-4xl font-black text-white tracking-tighter">{stat.value}</h3>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <motion.div variants={item} className="lg:col-span-2 glass-card rounded-[48px] p-12 relative overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h3 className="text-3xl font-black text-white tracking-tighter">Growth Velocity</h3>
                <p className="text-slate-500 font-medium mt-1">Institutional performance analytics</p>
              </div>
              <div className="flex bg-white/5 p-1.5 rounded-2xl backdrop-blur-xl border border-white/5">
                {['WK', 'MT', 'QT', 'YR'].map((t) => (
                  <button key={t} className={`px-5 py-2.5 rounded-xl text-[10px] font-black transition-all ${t === 'WK' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex-1 min-h-[300px] flex items-end gap-4 px-2">
              {[40, 60, 45, 85, 55, 95, 75, 80, 65, 70, 50, 90, 85, 75].map((h, i) => (
                <div key={i} className="flex-1 group relative">
                  <motion.div 
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    transition={{ delay: 0.8 + i * 0.04, duration: 1.5, ease: [0.23, 1, 0.32, 1] }}
                    className="w-full bg-gradient-to-t from-indigo-500/40 via-indigo-500 to-indigo-400 rounded-2xl group-hover:brightness-125 transition-all relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.1)_50%,transparent_100%)] translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  </motion.div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={item} className="glass-card rounded-[48px] p-12 flex flex-col">
            <h3 className="text-3xl font-black text-white tracking-tighter mb-10">Live Pulse</h3>
            <div className="space-y-8 flex-1">
              {[
                { user: 'Sarah Jenkins', action: 'completed Onboarding', time: 'Just now', icon: FiZap, color: 'indigo' },
                { user: 'David Miller', action: 'booked High-Ticket Call', time: '12m ago', icon: FiCalendar, color: 'emerald' },
                { user: 'Marcus Aurelius', action: 'reached 90% Progress', time: '45m ago', icon: FiTrendingUp, color: 'violet' },
                { user: 'Emma Stone', action: 'joined Platinum Mastermind', time: '2h ago', icon: FiUsers, color: 'rose' },
              ].map((activity, i) => (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 + i * 0.1 }}
                  key={i} 
                  className="flex items-center gap-5 group"
                >
                  <div className={`w-12 h-12 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-all duration-300`}>
                    <SafeIcon icon={activity.icon} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-white font-bold leading-tight group-hover:text-indigo-300 transition-colors">
                      {activity.user}
                    </p>
                    <p className="text-xs text-slate-500 font-medium">{activity.action}</p>
                    <span className="text-[10px] text-slate-600 font-black uppercase tracking-widest mt-1 block">{activity.time}</span>
                  </div>
                </motion.div>
              ))}
            </div>
            <button className="w-full mt-12 py-5 rounded-[2rem] bg-white/5 border border-white/5 text-slate-400 font-black hover:bg-indigo-600 hover:text-white hover:border-indigo-500 transition-all flex items-center justify-center gap-3 group">
              Intelligence Feed <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}